import type { ShellTexts } from '../../i18n/shell-texts';
import { fill } from '../../i18n/shell-texts';
import {
  EMPTY_IMAGE,
  type FormFieldCondition,
  type FormFieldSchema,
  type FormMode,
  type FormValue,
  type ImageFieldValue,
} from './schema';

const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function isEmpty(value: unknown): boolean {
  if (value !== null && typeof value === 'object' && 'preview' in value) {
    return (value as ImageFieldValue).preview === null;
  }
  return (
    value === undefined ||
    value === null ||
    (typeof value === 'string' && value.trim() === '') ||
    (Array.isArray(value) && value.length === 0)
  );
}

function holds(condition: FormFieldCondition, value: FormValue): boolean {
  const actual = value[condition.field];
  if ('eq' in condition && actual !== condition.eq) return false;
  if ('neq' in condition && actual === condition.neq) return false;
  if (condition.in && !condition.in.includes(actual)) return false;
  if (condition.notIn && condition.notIn.includes(actual)) return false;
  return true;
}

/** Is the field on screen in this mode, with these values? */
export function isFieldVisible(field: FormFieldSchema, mode: FormMode, value: FormValue): boolean {
  if (field.visibleInModes && !field.visibleInModes.includes(mode)) {
    return false;
  }
  return (field.visibleWhen ?? []).every(condition => holds(condition, value));
}

/**
 * The first problem with a field's value, worded from the catalogue, or null. Hidden fields are
 * never validated. Same rules and order as portal-ui-lib's `controlError`.
 */
export function fieldError(
  field: FormFieldSchema,
  value: unknown,
  texts: ShellTexts['kit']['validation'],
): string | null {
  if (field.type === 'notice') {
    return null;
  }
  if (field.required && (isEmpty(value) || (field.type === 'checkbox' && value !== true))) {
    return texts.required;
  }
  if (isEmpty(value)) {
    return null;
  }
  if (field.type === 'email' && typeof value === 'string' && !EMAIL.test(value.trim())) {
    return texts.email;
  }
  if (typeof value === 'string') {
    if (field.minLength !== undefined && value.length < field.minLength) {
      return fill(texts.minLength, { n: String(field.minLength) });
    }
    if (field.maxLength !== undefined && value.length > field.maxLength) {
      return fill(texts.maxLength, { n: String(field.maxLength) });
    }
    if (field.pattern !== undefined) {
      const pattern = typeof field.pattern === 'string' ? new RegExp(field.pattern) : field.pattern;
      if (!pattern.test(value)) {
        return texts.pattern;
      }
    }
  }
  if (field.type === 'number') {
    const num = typeof value === 'number' ? value : Number(value);
    if (Number.isNaN(num)) {
      return texts.invalid;
    }
    if (field.min !== undefined && num < field.min) {
      return fill(texts.min, { n: String(field.min) });
    }
    if (field.max !== undefined && num > field.max) {
      return fill(texts.max, { n: String(field.max) });
    }
  }
  return null;
}

/** Every visible field's error, by key; empty when the form can be submitted. */
export function formErrors(
  fields: readonly FormFieldSchema[],
  mode: FormMode,
  value: FormValue,
  texts: ShellTexts['kit']['validation'],
): Record<string, string> {
  const errors: Record<string, string> = {};
  for (const field of fields) {
    if (!isFieldVisible(field, mode, value)) {
      continue;
    }
    const error = fieldError(field, value[field.key], texts);
    if (error) {
      errors[field.key] = error;
    }
  }
  return errors;
}

/** The starting values: the initial value where given, else an empty value per field type. */
export function initialFormValue(
  fields: readonly FormFieldSchema[],
  initial: FormValue | null | undefined,
): FormValue {
  const value: FormValue = {};
  for (const field of fields) {
    if (field.type === 'image') {
      const start = initial?.[field.key];
      value[field.key] =
        typeof start === 'string' && start
          ? { ...EMPTY_IMAGE, preview: start }
          : start && typeof start === 'object'
            ? start
            : EMPTY_IMAGE;
    } else if (initial && field.key in initial) {
      value[field.key] = initial[field.key];
    } else if (field.type === 'checkbox') {
      value[field.key] = false;
    } else if (field.type === 'checklist' || field.type === 'repeater') {
      value[field.key] = [];
    } else if (field.type !== 'notice') {
      value[field.key] = field.type === 'number' ? null : '';
    }
  }
  return value;
}
